import React, { useMemo } from 'react';
import { useAsync } from 'react-async-hook';
import { commonService } from 'services';
import Space, { Spacer } from 'components/Space/Space';
import { Button, Checkbox, Divider, Dropdown } from 'antd';
import { Checkmark, ChevronDown } from '@carbon/icons-react';
import { useUrlParams } from 'hooks/useUrlParams';
import {
  FAMILY_BRANCH_ARRAY,
  FAMILY_IMPORT_STATUS_MAP,
} from 'containers/Family/AddApplication2/familyApplicationConstants';

export default function FamilyDashboardFilters({ fetchData, filters, setFilters, ClearFiltersButton }) {
  const { get: getUrlParams, update: updateUrlParams } = useUrlParams();

  const { result: statusOptions = [] } = useAsync(async () => {
    return commonService.get('/families/application-status-types/internal', {
      cache: { interpretHeader: false },
    });
  }, []);

  // Use FAMILY_BRANCH_ARRAY for branch filter options
  const branchOptions = FAMILY_BRANCH_ARRAY;

  const _thereAreFiltersApplied = useMemo(
    () =>
      filters?.statuses?.length || filters?.branchIds?.length || filters?.importStatuses?.length || filters?.testRecord,
    [filters],
  );

  const [branchIdsDropdownOpen, setBranchIdsDropdownOpen] = React.useState(false);
  const [temporaryBranchIds, setTemporaryBranchIds] = React.useState(filters?.branchIds);
  const _branchFilterApplied = !!filters?.branchIds?.length;

  const [statusDropdownOpen, setStatusDropdownOpen] = React.useState(false);
  const [temporaryStatuses, setTemporaryStatuses] = React.useState(filters?.statuses);
  const _statusFilterApplied = !!filters?.statuses?.length;

  const [importStatusesDropdownOpen, setImportStatusesDropdownOpen] = React.useState(false);
  const [temporaryImportStatuses, setTemporaryImportStatuses] = React.useState(filters?.importStatuses);
  const _importStatusesFilterApplied = !!filters?.importStatuses?.length;

  return (
    <Space className="m-7 flex-wrap">
      {/*
       * BRANCH filter
       */}
      <Dropdown
        trigger={['click']}
        open={branchIdsDropdownOpen}
        onOpenChange={(open) => {
          setBranchIdsDropdownOpen(open);
          setTemporaryBranchIds(filters?.branchIds);
        }}
        dropdownRender={() => (
          <div
            className="bg-white rounded-lg"
            style={{
              boxShadow: `0 6px 16px 0 rgba(0, 0, 0, 0.08),
            0 3px 6px -4px rgba(0, 0, 0, 0.12),
            0 9px 28px 8px rgba(0, 0, 0, 0.05)`,
            }}
          >
            <div className="p-5">
              <Checkbox.Group
                onChange={(_newValue) => {
                  setTemporaryBranchIds(_newValue);
                }}
                value={temporaryBranchIds}
              >
                <Space direction="vertical" vCentered={false}>
                  {branchOptions
                    ?.map((_option) => ({
                      text: _option.title,
                      value: _option.id,
                    }))
                    .map((option) => (
                      <Checkbox key={option.value} value={option.value}>
                        {option.text}
                      </Checkbox>
                    ))}
                </Space>
              </Checkbox.Group>
            </div>

            <Divider className="m-0" />

            <div className="py-4 px-5 flex">
              <Spacer />

              <Button
                size="small"
                type="primary"
                disabled={temporaryBranchIds === filters?.branchIds}
                onClick={() => {
                  setBranchIdsDropdownOpen(false);

                  const _newBranchIdsValue = temporaryBranchIds?.length ? temporaryBranchIds : undefined;
                  setFilters({
                    ...filters,
                    branchIds: _newBranchIdsValue,
                  });

                  // Update URL params
                  updateUrlParams({
                    filters: {
                      ...getUrlParams()?.filters,
                      testRecord: filters?.testRecord,
                      branchIds: _newBranchIdsValue?.join(','),
                    },
                  });

                  // Fetch new data
                  fetchData({
                    branchIds: _newBranchIdsValue,
                  });
                }}
              >
                Apply
              </Button>
            </div>
          </div>
        )}
      >
        <Button color={_branchFilterApplied ? 'primary' : 'default'} variant="filled" data-testid="filters__branchIds">
          <div className="flex gap-2 items-center">
            Branch
            {_branchFilterApplied && (
              <span className="w-7 h-5 rounded-full bg-primary text-white">{filters?.branchIds?.length}</span>
            )}
            <ChevronDown className="mr-[-3px] mb-[-2px]" />
          </div>
        </Button>
      </Dropdown>

      {/*
       * APP STATUS filter
       */}
      <Dropdown
        trigger={['click']}
        open={statusDropdownOpen}
        onOpenChange={(open) => {
          setStatusDropdownOpen(open);
          setTemporaryStatuses(filters?.statuses);
        }}
        dropdownRender={() => (
          <div
            className="bg-white rounded-lg"
            style={{
              boxShadow: `0 6px 16px 0 rgba(0, 0, 0, 0.08),
            0 3px 6px -4px rgba(0, 0, 0, 0.12),
            0 9px 28px 8px rgba(0, 0, 0, 0.05)`,
            }}
          >
            <div className="p-5">
              <Checkbox.Group
                onChange={(_newValue) => {
                  setTemporaryStatuses(_newValue);
                }}
                value={temporaryStatuses}
              >
                <Space direction="vertical" vCentered={false}>
                  {statusOptions?.map((_option) => (
                    <Checkbox key={_option.title} value={_option.title}>
                      {_option.description}
                    </Checkbox>
                  ))}
                </Space>
              </Checkbox.Group>
            </div>

            <Divider className="m-0" />

            <div className="py-4 px-5 flex">
              <Spacer />

              <Button
                size="small"
                type="primary"
                disabled={temporaryStatuses === filters?.statuses}
                onClick={() => {
                  setStatusDropdownOpen(false);

                  setFilters({
                    ...filters,
                    statuses: temporaryStatuses,
                  });

                  // Update URL params
                  updateUrlParams({
                    filters: {
                      ...getUrlParams()?.filters,
                      testRecord: filters?.testRecord,
                      statuses: temporaryStatuses?.join(','),
                    },
                  });

                  // Fetch new data
                  fetchData({
                    statuses: temporaryStatuses,
                  });
                }}
              >
                Apply
              </Button>
            </div>
          </div>
        )}
      >
        <Button color={_statusFilterApplied ? 'primary' : 'default'} variant="filled">
          <div className="flex gap-2 items-center">
            App Status
            {_statusFilterApplied && (
              <span className="w-7 h-5 rounded-full bg-primary text-white">{filters?.statuses?.length}</span>
            )}
            <ChevronDown className="mr-[-3px] mb-[-2px]" />
          </div>
        </Button>
      </Dropdown>

      {/*
       * IMPORT STATUS filter
       */}
      <Dropdown
        trigger={['click']}
        open={importStatusesDropdownOpen}
        onOpenChange={(open) => {
          setImportStatusesDropdownOpen(open);
          setTemporaryImportStatuses(filters?.importStatuses);
        }}
        dropdownRender={() => (
          <div
            className="bg-white rounded-lg"
            style={{
              boxShadow: `0 6px 16px 0 rgba(0, 0, 0, 0.08),
            0 3px 6px -4px rgba(0, 0, 0, 0.12),
            0 9px 28px 8px rgba(0, 0, 0, 0.05)`,
            }}
          >
            <div className="p-5">
              <Checkbox.Group
                onChange={(_newValue) => {
                  setTemporaryImportStatuses(_newValue);
                }}
                value={temporaryImportStatuses}
              >
                <Space direction="vertical" vCentered={false}>
                  {Object.values(FAMILY_IMPORT_STATUS_MAP).map((_option) => (
                    <Checkbox key={_option.value} value={_option.value}>
                      {_option.label}
                    </Checkbox>
                  ))}
                </Space>
              </Checkbox.Group>
            </div>

            <Divider className="m-0" />

            <div className="py-4 px-5 flex">
              <Spacer />

              <Button
                size="small"
                type="primary"
                disabled={temporaryImportStatuses === filters?.importStatuses}
                onClick={() => {
                  setImportStatusesDropdownOpen(false);

                  const _newImportStatusesValue = temporaryImportStatuses?.length ? temporaryImportStatuses : undefined;
                  setFilters({
                    ...filters,
                    importStatuses: _newImportStatusesValue,
                  });

                  // Update URL params
                  updateUrlParams({
                    filters: {
                      ...getUrlParams()?.filters,
                      testRecord: filters?.testRecord,
                      importStatuses: _newImportStatusesValue?.join(','),
                    },
                  });

                  // Fetch new data
                  fetchData({
                    importStatuses: _newImportStatusesValue,
                  });
                }}
              >
                Apply
              </Button>
            </div>
          </div>
        )}
      >
        <Button color={_importStatusesFilterApplied ? 'primary' : 'default'} variant="filled">
          <div className="flex gap-2 items-center">
            Import Status
            {_importStatusesFilterApplied && (
              <span className="w-7 h-5 rounded-full bg-primary text-white">{filters?.importStatuses?.length}</span>
            )}
            <ChevronDown className="mr-[-3px] mb-[-2px]" />
          </div>
        </Button>
      </Dropdown>

      {/*
       * INCLUDE TESTS filter
       */}
      {
        <Button
          color={filters?.testRecord ? 'primary' : 'default'}
          variant="filled"
          onClick={() => {
            setFilters({
              ...filters,
              testRecord: !!!filters?.testRecord,
            });

            updateUrlParams({
              filters: {
                ...getUrlParams()?.filters,
                testRecord: !!!filters?.testRecord || undefined,
              },
            });
            fetchData({
              testRecord: !!!filters?.testRecord,
            });
          }}
          aria-pressed={filters?.testRecord}
        >
          <div className="flex gap-2 items-center">
            Test accounts
            <span
              className={`w-5 h-5 border border-solid border-gray-300 rounded flex items-center justify-center pointer-events-none ${
                filters?.testRecord ? 'bg-primary border-primary' : 'bg-white'
              }`}
              role="presentation"
            >
              {filters?.testRecord && <Checkmark className="w-4 h-4 text-white" />}
            </span>
          </div>
        </Button>
      }

      {_thereAreFiltersApplied && <ClearFiltersButton data-testid="clear-filters" />}
    </Space>
  );
}
